"use client";

import { useState, useRef, useEffect } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { IoChevronBackOutline, IoArrowForwardOutline, IoWarningOutline } from 'react-icons/io5';
import { sendOtp, verifyOtp } from '../lib/authClient';
import { useAuth } from '../context/AuthContext';
import SectionHeading from '../components/SectionHeading';
import Breadcrumbs from './Breadcrumbs';
import { trackEvent } from '../lib/posthog';

const LoginPage = () => {
  const [step, setStep] = useState<1 | 2>(1);
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendTimer, setResendTimer] = useState(0);
  
  const { login, isLoggedIn } = useAuth();
  const router = useRouter();
  const searchParams = useSearchParams();
  const otpInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Get the redirect path from URL, default to dashboard
  const redirectPath = searchParams.get('redirect') || '/dashboard';

  // Redirect if already logged in
  useEffect(() => {
    if (isLoggedIn) {
      router.replace(redirectPath);
    }
  }, [isLoggedIn, router, redirectPath]);

  // Resend timer countdown
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  // Focus first OTP input when reaching step 2
  useEffect(() => {
    if (step === 2 && otpInputRefs.current[0]) {
      setTimeout(() => {
        otpInputRefs.current[0]?.focus();
      }, 100);
    }
  }, [step]);

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^\d]/g, '').substring(0, 10);
    setPhone(value);
    if (error) setError('');
  };

  const handleSendOtp = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    
    if (phone.length !== 10) {
      setError('Please enter a valid 10-digit mobile number');
      return;
    }

    setIsLoading(true);
    setError('');

    const { success, error: apiError } = await sendOtp(phone);

    setIsLoading(false);

    if (success) {
      trackEvent('user_otp_requested', { otp_delivery: 'sms' });
      setStep(2);
      setResendTimer(30);
      setOtp(['', '', '', '', '', '']);
    } else {
      setError(apiError || 'Failed to send OTP. Please try again.');
    }
  };

  const handleOtpChange = (index: number, value: string) => {
    // Only allow numbers
    const cleanValue = value.replace(/[^\d]/g, '');
    if (!cleanValue && value !== '') return;

    const newOtp = [...otp];
    // Handle pasting multiple digits
    if (cleanValue.length > 1) {
      const pastedDigits = cleanValue.substring(0, 6).split('');
      for (let i = 0; i < pastedDigits.length; i++) {
        if (index + i < 6) {
          newOtp[index + i] = pastedDigits[i];
        }
      }
      setOtp(newOtp);
      // Focus the next empty input or the last one
      const nextIndex = Math.min(index + pastedDigits.length, 5);
      otpInputRefs.current[nextIndex]?.focus();
      return;
    }

    newOtp[index] = cleanValue;
    setOtp(newOtp);
    if (error) setError('');

    // Auto-advance to next input
    if (cleanValue !== '' && index < 5) {
      otpInputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      // Focus previous input on backspace if current is empty
      otpInputRefs.current[index - 1]?.focus();
    } else if (e.key === 'Enter') {
      const fullOtp = otp.join('');
      if (fullOtp.length === 6) {
        handleVerifyOtp();
      }
    }
  };

  const handleVerifyOtp = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    const fullOtp = otp.join('');
    if (fullOtp.length < 4) {
      setError('Please enter the complete OTP');
      return;
    }

    setIsLoading(true);
    setError('');

    const { success, token, error: apiError } = await verifyOtp(phone, fullOtp);
    
    setIsLoading(false);

    if (success && token) {
      await login(phone, token);
      router.replace(redirectPath);
    } else {
      setError(apiError || 'Invalid OTP. Please try again.');
      // Clear OTP for retry and focus first box
      setOtp(['', '', '', '', '', '']);
      otpInputRefs.current[0]?.focus();
    }
  };

  return (
    <div className="relative min-h-[calc(100vh-8rem)] py-12 lg:py-20 flex items-center justify-center px-4 overflow-hidden bg-[#F9F9F9]">
      {/* Abstract liquid blobs for background */}
      <div className="absolute top-0 -left-20 w-72 h-72 lg:w-[500px] lg:h-[500px] bg-purple-300/40 rounded-full mix-blend-multiply filter blur-3xl lg:blur-[100px] pointer-events-none"></div>
      <div className="absolute top-40 -right-20 w-72 h-72 lg:w-[400px] lg:h-[400px] bg-orange-300/40 rounded-full mix-blend-multiply filter blur-3xl lg:blur-[100px] pointer-events-none"></div>
      <div className="absolute -bottom-20 left-1/2 w-80 h-80 lg:w-[600px] lg:h-[600px] bg-pink-300/30 rounded-full mix-blend-multiply filter blur-3xl lg:blur-[100px] pointer-events-none"></div>

      <div className="w-full max-w-md relative z-10">
        <Breadcrumbs items={[{ label: 'Login' }]} className="mb-4 justify-center" />
        <div className="text-center mb-8 relative z-10">
          <SectionHeading
            title={step === 1 ? "Login to Your Account" : "Verify Your Number"}
            subtitle={step === 1 ? "Enter your mobile number to get started" : `We sent a code to +91 ${phone}`}
          />
        </div>

        <div className="backdrop-blur-xl bg-white/70 p-6 sm:p-8 rounded-3xl shadow-[0_8px_32px_0_rgba(31,38,135,0.07)] border border-white/60 animate-drop-in">
          {error && (
            <div className="mb-6 flex items-start gap-3 bg-red-50 text-red-600 p-4 rounded-xl text-sm font-medium border border-red-100 animate-fade-in">
              <IoWarningOutline className="text-xl flex-shrink-0 mt-0.5" />
              <p>{error}</p>
            </div>
          )}

          {step === 1 ? (
            <form onSubmit={handleSendOtp} className="space-y-6">
              <div>
                <label htmlFor="phone" className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">
                  Mobile Number
                </label>
                <div className="relative flex items-center">
                  <div className="absolute left-0 top-0 bottom-0 flex items-center justify-center pl-4 pr-3 text-gray-500 font-bold border-r border-gray-200 bg-gray-50 rounded-l-xl">
                    +91
                  </div>
                  <input
                    id="phone"
                    type="tel"
                    inputMode="numeric"
                    autoComplete="tel-national"
                    value={phone}
                    onChange={handlePhoneChange}
                    className={`ui-input pl-[4.5rem] py-4 h-[60px] text-xl tracking-wide font-medium rounded-xl transition-all ${
                      error ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20' : 'focus:border-primary focus:ring-primary/20'
                    }`}
                    placeholder="Enter 10 digits"
                    disabled={isLoading}
                    autoFocus
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading || phone.length !== 10}
                className="ui-button-accent w-full h-[60px] text-lg rounded-xl shadow-lg hover:shadow-xl group"
              >
                {isLoading ? (
                  <span className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    Send OTP <IoArrowForwardOutline className="group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </button>
            </form>
          ) : (
            <div className="space-y-6 animate-fade-in">
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-bold text-gray-700 uppercase tracking-wide">
                  Enter 6-digit Code
                </label>
                <button 
                  onClick={() => setStep(1)}
                  className="text-primary text-sm font-semibold hover:underline flex items-center gap-1"
                  disabled={isLoading}
                >
                  <IoChevronBackOutline /> Change
                </button>
              </div>

              <div className="flex justify-between gap-2 sm:gap-3">
                {otp.map((digit, idx) => (
                  <input
                    key={idx}
                    ref={(el) => { otpInputRefs.current[idx] = el; }}
                    type="tel"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(idx, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(idx, e)}
                    disabled={isLoading}
                    className={`w-full aspect-square text-center text-2xl font-bold rounded-xl border-2 transition-all ${
                      error ? 'border-red-300 bg-red-50 text-red-700' : 'border-gray-200 bg-white focus:border-primary focus:ring-4 focus:ring-primary/10'
                    }`}
                  />
                ))}
              </div>

              <button
                onClick={handleVerifyOtp}
                disabled={isLoading || otp.join('').length < 4}
                className="ui-button-accent w-full h-[60px] text-lg rounded-xl shadow-lg hover:shadow-xl mt-8"
              >
                {isLoading ? (
                  <span className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  'Verify OTP'
                )}
              </button>

              <div className="text-center pt-2">
                <p className="text-sm text-gray-500 font-medium">
                  Didn't receive the code?{' '}
                  {resendTimer > 0 ? (
                    <span className="text-gray-400">Resend in {resendTimer}s</span>
                  ) : (
                    <button 
                      onClick={handleSendOtp}
                      disabled={isLoading}
                      className="text-primary font-bold hover:underline transition-all"
                    >
                      Resend OTP
                    </button>
                  )}
                </p>
              </div>
            </div>
          )}
        </div>
        
        {/* Trust Indicators */}
        <div className="mt-8 text-center text-sm font-medium text-gray-400">
          <p>Secure login with OTP validation.</p>
          <p>Your data is protected and kept private.</p>
        </div>
      </div>
    </div>
  );
};

export default function LoginClient() {
  return <LoginPage />;
}
